import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Chess, type Square } from 'chess.js'
import { Chessboard } from 'react-chessboard'
import { useMutation } from '@tanstack/react-query'
import {
  evaluateMove,
  hintSquare,
  parseUci,
  resolveSquareClick,
  toUci,
  type LegalMove,
} from '@shess/engine'
import type { PuzzleView, SquareName, UciMove } from '@shess/schemas'
import { cssSafeId } from '../dom-id.ts'
import { useTRPC } from '../trpc.ts'

/** Long enough to read the position before the opponent's move lands. */
const SETUP_DELAY_MS = 550
/** Lets your own move settle visually before the reply comes back. */
const REPLY_DELAY_MS = 250
/** Long enough to see a wrong move land before it is taken back. */
const BOUNCE_DELAY_MS = 400

/**
 * `retry` is the interesting one: the attempt is already scored and closed, but
 * the player keeps working the line. Being wrong costs rating once, not the
 * chance to finish.
 */
type Phase =
  | 'setup'
  | 'playing'
  | 'thinking'
  | 'retry'
  | 'solved'
  | 'finished'
  | 'revealed'

interface Props {
  puzzle: PuzzleView
  attemptId: string
  onAdvance: () => void
}

export function PuzzleBoard({ puzzle, attemptId, onAdvance }: Props) {
  const trpc = useTRPC()
  const game = useRef<Chess | null>(null)
  /** Bumped per puzzle so a late response can't land on the next position. */
  const token = useRef(0)

  const [position, setPosition] = useState(puzzle.fen)
  const [phase, setPhase] = useState<Phase>('setup')
  const [lastMove, setLastMove] = useState<{ from: string; to: string } | null>(null)
  const [hintFrom, setHintFrom] = useState<string | null>(null)
  const [solution, setSolution] = useState<string[] | null>(null)
  const [ratingDelta, setRatingDelta] = useState<number | null>(null)
  /** Correct moves found so far. A ref, because handlers must never read a
   *  stale count, and nothing renders from it. */
  const movesMade = useRef(0)
  /**
   * Guards against two handlers submitting one gesture. This cannot be React
   * state: state does not update until the next render, so both callers would
   * still see 'playing' and both would submit.
   */
  const pending = useRef(false)
  const [missed, setMissed] = useState(false)
  const [selected, setSelected] = useState<SquareName | null>(null)
  const [legalMoves, setLegalMoves] = useState<LegalMove[]>([])
  const boardRef = useRef<HTMLDivElement | null>(null)

  const clearSelection = useCallback(() => {
    setSelected(null)
    setLegalMoves([])
  }, [])

  const submitMove = useMutation(trpc.attempt.submitMove.mutationOptions())
  const hint = useMutation(trpc.attempt.hint.mutationOptions())
  const skip = useMutation(trpc.attempt.skip.mutationOptions())

  // Start each puzzle from the position before the setup move, then play that
  // move — it's what tells you whose turn it is and why the position matters.
  useEffect(() => {
    const board = new Chess(puzzle.fen)
    const current = ++token.current
    game.current = board

    setPosition(puzzle.fen)
    setPhase('setup')
    setLastMove(null)
    setHintFrom(null)
    setSolution(null)
    setRatingDelta(null)
    movesMade.current = 0
    pending.current = false
    setMissed(false)

    const timer = setTimeout(() => {
      if (token.current !== current) return
      const setup = parseUci(puzzle.setupMove)
      board.move(setup)
      setPosition(board.fen())
      setLastMove({ from: setup.from, to: setup.to })
      setPhase('playing')
    }, SETUP_DELAY_MS)

    return () => clearTimeout(timer)
  }, [puzzle.id, puzzle.fen, puzzle.setupMove, attemptId])

  const resolveMove = useCallback(
    async (move: UciMove, fenBefore: string, current: number) => {
      const board = game.current
      if (!board) return

      const revert = () => {
        if (token.current !== current) return
        board.load(fenBefore)
        setPosition(fenBefore)
        setPhase('playing')
      }

      try {
        const result = await submitMove.mutateAsync({
          attemptId,
          move,
          movesMade: movesMade.current,
        })
        if (token.current !== current) return

        if (result.status === 'correct') {
          await new Promise((resolve) => setTimeout(resolve, REPLY_DELAY_MS))
          if (token.current !== current) return
          const reply = parseUci(result.reply)
          board.move(reply)
          setPosition(board.fen())
          setLastMove({ from: reply.from, to: reply.to })
          movesMade.current += 1
          setPhase('playing')
          return
        }

        if (result.status === 'solved') {
          movesMade.current += 1
          setRatingDelta(result.rating.delta)
          setPhase('solved')
          return
        }

        // Wrong. The rating is spent and the attempt is closed, but the puzzle
        // is still worth finishing, so hand the line to the client and let the
        // player keep going. The solution is not shown unless they ask.
        board.load(fenBefore)
        setPosition(fenBefore)
        setLastMove(null)
        setSolution(result.solution)
        setRatingDelta(result.rating.delta)
        setMissed(true)
        setPhase('retry')
      } catch {
        revert()
      } finally {
        pending.current = false
      }
    },
    [attemptId, submitMove],
  )

  /**
   * The same judgement the server makes, run locally once the attempt is over.
   * Reusing `evaluateMove` means a retry accepts an alternate mate exactly as
   * the live attempt would have, instead of quietly diverging from it.
   */
  const practise = useCallback(
    async (move: UciMove, fenBefore: string, current: number) => {
      const board = game.current
      if (!board || !solution) {
        pending.current = false
        return
      }

      try {
        const step = evaluateMove({ fen: puzzle.fen, solution }, movesMade.current, move)

        if (step.status === 'incorrect') {
          await new Promise((resolve) => setTimeout(resolve, BOUNCE_DELAY_MS))
          if (token.current !== current) return
          board.load(fenBefore)
          setPosition(fenBefore)
          setLastMove(null)
          setPhase('retry')
          return
        }

        if (step.status === 'correct') {
          await new Promise((resolve) => setTimeout(resolve, REPLY_DELAY_MS))
          if (token.current !== current) return
          const reply = parseUci(step.reply)
          board.move(reply)
          setPosition(board.fen())
          setLastMove({ from: reply.from, to: reply.to })
          movesMade.current += 1
          setPhase('retry')
          return
        }

        movesMade.current += 1
        setPhase('finished')
      } finally {
        pending.current = false
      }
    },
    [solution, puzzle.fen],
  )

  /** The single path a move takes, however the player made it. */
  const playMove = useCallback(
    (from: SquareName, to: SquareName): boolean => {
      const board = game.current
      // pending is checked synchronously: by the time React re-renders with
      // phase 'thinking', a duplicate handler has already run.
      if (!board || pending.current || (phase !== 'playing' && phase !== 'retry')) {
        return false
      }

      const moving = board.get(from as Square)
      const lastRank = to.endsWith('8') || to.endsWith('1')
      // Promotion always takes a queen for now; underpromotion puzzles are rare
      // and the board has no picker yet.
      const promotion = moving?.type === 'p' && lastRank ? ('q' as const) : undefined

      const fenBefore = board.fen()
      try {
        board.move({ from, to, promotion })
      } catch {
        // Illegal move — snap back without troubling the server.
        return false
      }

      pending.current = true
      setPosition(board.fen())
      setLastMove({ from, to })
      setHintFrom(null)
      clearSelection()
      setPhase('thinking')

      const uci = toUci({ from, to, promotion })
      if (phase === 'retry') {
        void practise(uci, fenBefore, token.current)
      } else {
        void resolveMove(uci, fenBefore, token.current)
      }
      return true
    },
    [phase, resolveMove, practise, clearSelection],
  )

  const onPieceDrop = useCallback(
    ({ sourceSquare, targetSquare }: { sourceSquare: string; targetSquare: string | null }) => {
      const moved =
        targetSquare !== null &&
        playMove(sourceSquare as SquareName, targetSquare as SquareName)
      // Dropped somewhere it cannot go: treat it like clicking an illegal
      // square and put the piece down.
      if (!moved) clearSelection()
      return moved
    },
    [playMove, clearSelection],
  )

  /**
   * Picking a piece up should say the same thing as clicking it, so the
   * destinations show from the moment a drag starts.
   */
  const onPieceDrag = useCallback(
    ({ square }: { square: string | null }) => {
      const board = game.current
      if (!board || !square || (phase !== 'playing' && phase !== 'retry')) return

      // Resolved against no prior selection, so this is just "select it if it
      // is yours" — including when some other piece was already selected.
      const outcome = resolveSquareClick(board.fen(), null, square as SquareName)
      if (outcome.action === 'select') {
        setSelected(outcome.square)
        setLegalMoves(outcome.moves)
      }
    },
    [phase],
  )

  const onSquareClick = useCallback(
    ({ square }: { square: string }) => {
      const board = game.current
      if (!board || (phase !== 'playing' && phase !== 'retry')) return

      const outcome = resolveSquareClick(board.fen(), selected, square as SquareName)
      switch (outcome.action) {
        case 'select':
          setSelected(outcome.square)
          setLegalMoves(outcome.moves)
          break
        case 'move':
          playMove(outcome.from, outcome.to)
          break
        case 'deselect':
          clearSelection()
          break
        case 'ignore':
          break
      }
    },
    [phase, selected, playMove, clearSelection],
  )

  // Escape cancels, the way it does everywhere else.
  useEffect(() => {
    if (!selected) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') clearSelection()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [selected, clearSelection])

  // A press anywhere outside the board cancels too. Pointer-down rather than
  // click so it feels immediate, and clicks inside the board are left alone for
  // onSquareClick to interpret.
  useEffect(() => {
    if (!selected) return
    const onPointerDown = (event: PointerEvent) => {
      if (!boardRef.current?.contains(event.target as Node)) clearSelection()
    }
    window.addEventListener('pointerdown', onPointerDown)
    return () => window.removeEventListener('pointerdown', onPointerDown)
  }, [selected, clearSelection])

  // Nothing stays selected once it is not the player's turn to move.
  useEffect(() => {
    if (phase !== 'playing' && phase !== 'retry') clearSelection()
  }, [phase, clearSelection])

  const squareStyles = useMemo(() => {
    const styles: Record<string, React.CSSProperties> = {}
    const paint = (square: string, style: React.CSSProperties) => {
      styles[square] = { ...styles[square], ...style }
    }

    if (lastMove) {
      paint(lastMove.from, { backgroundColor: 'rgba(250, 204, 21, 0.22)' })
      paint(lastMove.to, { backgroundColor: 'rgba(250, 204, 21, 0.28)' })
    }
    if (hintFrom) {
      paint(hintFrom, {
        backgroundColor: 'rgba(16, 185, 129, 0.35)',
        boxShadow: 'inset 0 0 0 3px rgb(16 185 129)',
      })
    }
    if (selected) {
      paint(selected, { backgroundColor: 'rgba(56, 189, 248, 0.45)' })
    }

    // backgroundImage, not background: the marker sits on top of whatever tint
    // the square already had rather than replacing it.
    for (const move of legalMoves) {
      paint(move.to, {
        backgroundImage: move.captures
          ? 'radial-gradient(circle, transparent 56%, rgba(56, 189, 248, 0.6) 58%, rgba(56, 189, 248, 0.6) 74%, transparent 76%)'
          : 'radial-gradient(circle, rgba(56, 189, 248, 0.65) 19%, transparent 21%)',
      })
    }

    return styles
  }, [lastMove, hintFrom, selected, legalMoves])

  const over = phase === 'solved' || phase === 'finished' || phase === 'revealed'
  const canMove = phase === 'playing' || phase === 'retry'

  const solutionSan = useMemo(() => {
    if (!solution) return null
    const board = new Chess(puzzle.fen)
    const moves: string[] = []
    for (const uci of solution) {
      const played = board.move(parseUci(uci))
      moves.push(played.san)
    }
    // Drop the opponent's setup move — the player never had to find it.
    return moves.slice(1)
  }, [solution, puzzle.fen])

  return (
    <div className="flex w-full flex-col items-center gap-4">
      <div ref={boardRef} className="w-full max-w-[min(70vh,34rem)]">
        <Chessboard
          options={{
            position,
            boardOrientation: puzzle.playerColor,
            onPieceDrop,
            onPieceDrag,
            onPieceDragCancel: clearSelection,
            onSquareClick,
            squareStyles,
            allowDragging: phase === 'playing' || phase === 'retry',
            canDragPiece: ({ square }) => {
              const board = game.current
              if (!board || !square) return false
              const piece = board.get(square as Square)
              return piece ? piece.color === board.turn() : false
            },
            animationDurationInMs: 220,
            darkSquareStyle: { backgroundColor: '#4a6741' },
            lightSquareStyle: { backgroundColor: '#e8e4d0' },
            // Never the raw puzzle id — see cssSafeId.
            id: cssSafeId('puzzle', puzzle.id),
          }}
        />
      </div>

      <StatusLine
        phase={phase}
        playerColor={puzzle.playerColor}
        ratingDelta={ratingDelta}
        solution={solutionSan}
        missed={missed}
      />

      <div className="flex flex-wrap items-center justify-center gap-2">
        {over ? (
          <button
            onClick={onAdvance}
            autoFocus
            className="rounded-lg bg-emerald-600 px-5 py-2 font-medium text-white transition hover:bg-emerald-500"
          >
            Next puzzle
          </button>
        ) : (
          <>
            <button
              disabled={!canMove || hint.isPending}
              onClick={() => {
                // The attempt is closed during retry, so the server would
                // refuse; the line is already here, so ask it instead.
                if (phase === 'retry') {
                  if (solution) {
                    setHintFrom(hintSquare({ fen: puzzle.fen, solution }, movesMade.current))
                  }
                  return
                }
                hint.mutate({ attemptId }, { onSuccess: (data) => setHintFrom(data.from) })
              }}
              className="rounded-lg border border-stone-700 px-4 py-2 text-sm transition hover:border-stone-500 disabled:opacity-40"
            >
              Hint
            </button>
            <button
              disabled={!canMove || skip.isPending}
              onClick={() => {
                if (phase === 'retry') {
                  setPhase('revealed')
                  return
                }
                skip.mutate(
                  { attemptId },
                  {
                    onSuccess: (data) => {
                      setSolution(data.solution)
                      setPhase('revealed')
                    },
                  },
                )
              }}
              className="rounded-lg border border-stone-700 px-4 py-2 text-sm text-stone-400 transition hover:border-stone-500 disabled:opacity-40"
            >
              Show solution
            </button>
            {phase === 'retry' ? (
              <button
                onClick={onAdvance}
                className="rounded-lg border border-stone-800 px-4 py-2 text-sm text-stone-500 transition hover:border-stone-600 hover:text-stone-300"
              >
                Skip ahead
              </button>
            ) : null}
          </>
        )}
      </div>
    </div>
  )
}

function StatusLine({
  phase,
  playerColor,
  ratingDelta,
  solution,
  missed,
}: {
  phase: Phase
  playerColor: 'white' | 'black'
  ratingDelta: number | null
  solution: string[] | null
  missed: boolean
}) {
  const delta =
    ratingDelta === null ? null : (
      <span className={ratingDelta >= 0 ? 'text-emerald-400' : 'text-rose-400'}>
        {ratingDelta >= 0 ? '+' : ''}
        {ratingDelta}
      </span>
    )

  if (phase === 'setup') {
    return <p className="h-6 text-sm text-stone-500">…</p>
  }

  if (phase === 'playing') {
    return (
      <p className="h-6 text-sm text-stone-300">
        <span className="font-medium capitalize">{playerColor}</span> to play — find the
        best move.
      </p>
    )
  }

  if (phase === 'thinking') {
    return <p className="h-6 text-sm text-stone-500">{missed ? '…' : 'Checking…'}</p>
  }

  if (phase === 'solved') {
    return <p className="h-6 text-sm font-medium text-emerald-400">Solved. {delta}</p>
  }

  // Scored and closed, but still playable. Say so plainly, so it is clear the
  // rating has already moved and nothing further is at stake.
  if (phase === 'retry') {
    return (
      <p className="h-6 text-sm">
        <span className="font-medium text-rose-400">Not quite.</span> {delta}
        <span className="ml-2 text-stone-400">Keep going — finish the line.</span>
      </p>
    )
  }

  if (phase === 'finished') {
    return (
      <p className="h-6 text-sm">
        <span className="font-medium text-sky-400">Found it.</span>
        <span className="ml-2 text-stone-500">Rating already counted.</span>
      </p>
    )
  }

  return (
    <div className="flex h-6 items-center gap-2 text-sm">
      <span className="text-stone-400">Solution:</span>
      {solution ? (
        <span className="font-mono text-stone-300">{solution.join(' ')}</span>
      ) : null}
    </div>
  )
}
